import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ApplicationCard from './components/ApplicationCard';
import MetricCard from './components/MetricCard';
import NotificationItem from './components/NotificationItem';
import DocumentCard from './components/DocumentCard';
import QuickActionCard from './components/QuickActionCard';

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user, isLoaded } = useUser();

  const [applications] = useState([]);

  const [metrics] = useState([
  {
    title: "Total Applications",
    value: "0",
    icon: "FileText",
    trend: "neutral",
    trendValue: "No applications yet",
    color: "primary"
  },
  {
    title: "Under Review",
    value: "0",
    icon: "Clock",
    trend: "neutral",
    trendValue: "No change",
    color: "warning"
  },
  {
    title: "Accepted",
    value: "0",
    icon: "CheckCircle",
    trend: "neutral",
    trendValue: "No acceptances yet",
    color: "success"
  },
  {
    title: "Upcoming Deadlines",
    value: "0",
    icon: "Calendar",
    trend: "neutral",
    trendValue: "No deadlines",
    color: "accent"
  }]
  );

  const [notifications, setNotifications] = useState([]);

  const [documents] = useState([]);

  const quickActions = [
  {
    title: "Start New Application",
    description: "Begin applying to a Chinese language program",
    icon: "PenTool",
    path: "/institution-search",
    color: "primary"
  },
  {
    title: "Find Programs",
    description: "Search universities and language courses",
    icon: "Search",
    path: "/institution-search",
    color: "secondary"
  },
  {
    title: "Upload Documents",
    description: "Manage your application documents",
    icon: "Upload",
    path: "/user-dashboard",
    color: "accent"
  },
  {
    title: "Profile Settings",
    description: "Update your personal information",
    icon: "Settings",
    path: "/user-dashboard",
    color: "success"
  }];


  const handleDismissNotification = (id) => {
    setNotifications(notifications?.filter((n) => n?.id !== id));
  };

  const [activeTab, setActiveTab] = useState('active');

  const filteredApplications = applications?.filter((app) => {
    if (activeTab === 'active') return app?.status === 'draft' || app?.status === 'submitted';
    if (activeTab === 'accepted') return app?.status === 'accepted';
    if (activeTab === 'archived') return app?.status === 'rejected';
    return true;
  });

  // Get user's first name or fallback
  const userName = user?.firstName || user?.username || 'User';

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Breadcrumbs />
      <main className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12 mt-20">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
            Welcome, {userName}
          </h1>
          <p className="text-sm md:text-base text-muted-foreground font-caption">
            Manage your Chinese language program applications and track your progress
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
          {metrics?.map((metric, index) =>
          <MetricCard key={index} {...metric} />
          )}
        </div>

        {notifications?.length > 0 &&
        <div className="mb-6 md:mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg md:text-xl lg:text-2xl font-heading font-semibold text-foreground">
                Important Notifications
              </h2>
              <Button variant="ghost" size="sm" iconName="Bell" iconPosition="left">
                View All
              </Button>
            </div>
            <div className="space-y-3 md:space-y-4">
              {notifications?.map((notification) =>
            <NotificationItem
              key={notification?.id}
              notification={notification}
              onDismiss={handleDismissNotification} />

            )}
            </div>
          </div>
        }

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8">
          <div className="lg:col-span-2">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 md:mb-6">
              <h2 className="text-lg md:text-xl lg:text-2xl font-heading font-semibold text-foreground">
                My Applications
              </h2>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={() => navigate('/institution-search')}>

                New Application
              </Button>
            </div>

            <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
              {['active', 'accepted', 'archived']?.map((tab) =>
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`
                    px-4 py-2 rounded-md font-medium text-sm font-caption whitespace-nowrap transition-smooth
                    ${activeTab === tab ?
                'bg-primary text-primary-foreground' :
                'bg-muted text-muted-foreground hover:bg-muted/80'}
                  `
                }>

                  {tab?.charAt(0)?.toUpperCase() + tab?.slice(1)}
                </button>
              )}
            </div>

            <div className="space-y-4 md:space-y-6">
              {filteredApplications?.length > 0 ?
              filteredApplications?.map((application) =>
              <ApplicationCard key={application?.id} application={application} />
              ) :

              <div className="bg-card rounded-lg border border-border p-8 md:p-12 text-center">
                  <Icon name="FileText" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                    No Applications Found
                  </h3>
                  <p className="text-sm text-muted-foreground font-caption mb-4">
                    Start your journey by applying to a Chinese language program
                  </p>
                  <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => navigate('/institution-search')}>

                    Start New Application
                  </Button>
                </div>
              }
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-card rounded-lg border border-border p-4 md:p-6 shadow-warm-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">
                  Quick Actions
                </h3>
                <Icon name="Zap" size={20} color="var(--color-accent)" />
              </div>
              <div className="space-y-3">
                {quickActions?.map((action, index) =>
                <QuickActionCard key={index} {...action} />
                )}
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-4 md:p-6 shadow-warm-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">
                  Document Status
                </h3>
                <Button variant="ghost" size="sm" iconName="Upload" iconPosition="left">
                  Upload
                </Button>
              </div>
              <div className="space-y-3">
                {documents?.map((document) =>
                <DocumentCard key={document?.id} document={document} />
                )}
              </div>
            </div>

            <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-lg border border-primary/20 p-4 md:p-6">
              <div className="flex items-start gap-3 mb-4">
                <Icon name="Lightbulb" size={24} color="var(--color-primary)" />
                <div>
                  <h3 className="text-base font-heading font-semibold text-foreground mb-2">
                    Application Tips
                  </h3>
                  <ul className="space-y-2 text-xs md:text-sm text-muted-foreground font-caption">
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                      <span>Submit applications at least 3 months before program start</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                      <span>Ensure all documents are certified and translated</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                      <span>Check visa requirements for your country</span>
                    </li>
                  </ul>
                </div>
              </div>
              <Button variant="outline" size="sm" fullWidth iconName="ExternalLink" iconPosition="right">
                View Complete Guide
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>);

};

export default UserDashboard;